import { FC, useCallback, useEffect, useRef, useState } from "react";

import "@livekit/components-styles";
import {
  ControlBar,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  TrackLoop,
  useConnectionState,
  useParticipants,
  useRoomContext,
  useTracks,
} from "@livekit/components-react";
import {
  AccessTime,
  CameraAlt,
  Close,
  KeyboardArrowDown,
  Logout,
  Mic,
  MicOff,
  Pause,
  PeopleOutline,
  PersonAddAlt1Outlined,
  PersonRemove,
  PlayArrow,
  ShieldOutlined,
  VideoCall,
  Videocam,
  VideocamOff,
  Warning,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  darken,
  Dialog,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ConnectAuditEventModel,
  ConnectInvitationModel,
  ConnectLocationPingModel,
  ConnectMeetingModel,
  getConnectAudit,
  getConnectImages,
  getConnectInvitations,
  getConnectLocations,
  postConnectImage,
  postConnectInvitationDecision,
  postConnectMeetingEnd,
  postConnectMeetingPause,
  postConnectMeetingToken,
  postConnectParticipantCameraRequest,
  postConnectParticipantMedia,
} from "@rubi/api-generated/clientRs";
import {
  DisconnectOnPageHide,
  isLostConnectionQuality,
  isReconnectingConnectionState,
  toast,
} from "@rubi/ui";
import { isChromiumUserAgent } from "@rubi/utils";
import {
  AudioPresets,
  RoomEvent,
  type RoomOptions,
  Track,
  VideoPresets,
} from "livekit-client";
import { useTranslation } from "react-i18next";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { useDevEnvironment } from "@/contexts/DevContext";
import { APP_LIVEKIT_FORCE_RELAY } from "@/environment";

import { consoleMsgFrom } from "./apiError";
import ConfirmDialog from "./ConfirmDialog";
import InvitationsDialog from "./InvitationsDialog";
import OfficerBreakRoom from "./OfficerBreakRoom";
import PausePickerDialog from "./PausePickerDialog";

// React 18/19 JSX compatibility casts (mirrors the services app).
const LKRoom = LiveKitRoom as any;
const LKParticipantTile = ParticipantTile as any;
const LKTrackLoop = TrackLoop as any;

const LK_CONNECT_OPTIONS =
  APP_LIVEKIT_FORCE_RELAY && isChromiumUserAgent(navigator.userAgent)
    ? { rtcConfig: { iceTransportPolicy: "relay" as const } }
    : undefined;

/**
 * Caps what each participant publishes and receives. `video`/`audio` on
 * <LiveKitRoom> are plain booleans, so camera and mic inherit these defaults —
 * pass capture/publish options there and they override per track.
 */
const LK_ROOM_OPTIONS: RoomOptions = {
  /**
   * LiveKit's own page-leave handler disconnects before our
   * beforeunload confirm resolves — this avoids that race.
   */
  disconnectOnPageLeave: false,
  adaptiveStream: true,
  dynacast: true,
  videoCaptureDefaults: {
    resolution: VideoPresets.h720.resolution,
  },
  publishDefaults: {
    simulcast: true,
    videoEncoding: { maxBitrate: 1_200_000, maxFramerate: 24 },
    videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
    screenShareEncoding: { maxBitrate: 1_500_000, maxFramerate: 15 },
    audioPreset: AudioPresets.speech,
    dtx: true,
    // Protects audio against packet loss.
    red: true,
  },
};

const INVITEE_PREFIX = "invitee-";

// Interruption / attention events surfaced in the Monitor panel (Phase J/I/L).
const ALERT_LABEL_KEYS: Record<string, string> = {
  participant_disconnected: "officerRoom.alerts.participantDisconnected",
  participant_reconnected: "officerRoom.alerts.participantReconnected",
  participant_media_off: "officerRoom.alerts.participantMediaOff",
  participant_media_on: "officerRoom.alerts.participantMediaOn",
  location_unavailable: "officerRoom.alerts.locationUnavailable",
  location_restored: "officerRoom.alerts.locationRestored",
  pause_requested: "officerRoom.alerts.pauseRequested",
  pause_request_cancelled: "officerRoom.alerts.pauseRequestCancelled",
};

// Pre-waiting-room states: shown read-only (no admit/decline) so the officer
// sees who's been invited and how far along they are, without prematurely
// admitting someone who hasn't actually arrived in the waiting room yet.
const JOINING_STATUS_LABEL_KEYS: Record<string, string> = {
  invited: "officerRoom.joiningStatus.invited",
  link_opened: "officerRoom.joiningStatus.linkOpened",
  permissions_pending: "officerRoom.joiningStatus.permissionsPending",
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  invited: "officerRoom.statuses.invited",
  link_opened: "officerRoom.statuses.linkOpened",
  permissions_pending: "officerRoom.statuses.permissionsPending",
  waiting_room: "officerRoom.statuses.waitingRoom",
  admitted: "officerRoom.statuses.admitted",
  paused: "officerRoom.statuses.paused",
  removed: "officerRoom.statuses.removed",
  declined: "officerRoom.statuses.declined",
  left: "officerRoom.statuses.left",
  reconnected: "officerRoom.statuses.reconnected",
};

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  meeting: ConnectMeetingModel | null;
}

function useSessionClock(active: boolean) {
  const [start] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  const s = Math.max(0, Math.floor((now - start) / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return {
    elapsed: `${hh}:${mm}:${ss}`,
    clock: new Date(now).toLocaleTimeString(),
  };
}

/** Own connection dropped: keep the room mounted and say so (D12). */
function ReconnectingBanner() {
  const { t } = useTranslation("connect");
  const state = useConnectionState();
  if (!isReconnectingConnectionState(state)) return null;
  return (
    <Box
      role="status"
      sx={{
        px: 2,
        py: 0.75,
        bgcolor: "#3b2f0a",
        color: "#ffe1a3",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <Warning fontSize="small" />
      {t("officerRoom.reconnectingBanner")}
    </Box>
  );
}

// True only if the interviewer is included in the pause — a selective pause
// of other participants must not reroute the interviewer's own disconnect.
export function isPausedForInterviewer(metadata: string | undefined): boolean {
  try {
    const data = JSON.parse(metadata ?? "");
    if (!data.paused) return false;
    const target = data.target;
    if (!target || target.kind === "all") return true;
    if (target.kind === "selected") return !!target.include_interviewer;
    return false;
  } catch {
    return false;
  }
}

/**
 * Reads room.metadata directly (not React state) so a pause-triggered
 * disconnect can't race ahead of the "paused" flag reaching this client.
 */
export function DisconnectRouter({
  onPaused,
  onDisconnected,
}: {
  onPaused: () => void;
  onDisconnected: () => void;
}) {
  const room = useRoomContext();
  useEffect(() => {
    const handleDisconnected = () => {
      if (isPausedForInterviewer(room.metadata)) {
        onPaused();
      } else {
        onDisconnected();
      }
    };
    room.on(RoomEvent.Disconnected, handleDisconnected);
    return () => {
      room.off(RoomEvent.Disconnected, handleDisconnected);
    };
  }, [room, onPaused, onDisconnected]);
  return null;
}

/** A remote participant whose signal dropped shows "Reconnecting…" over the
 *  tile instead of the tile vanishing (D12). */
function TileReconnecting() {
  const { t } = useTranslation("connect");
  const participants = useParticipants();
  const lost = participants.filter(
    (p) => !p.isLocal && isLostConnectionQuality(p.connectionQuality),
  );
  if (lost.length === 0) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 5,
        display: "flex",
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      {lost.map((p) => (
        <Chip
          key={p.identity}
          size="small"
          icon={<Warning sx={{ fontSize: 14, color: "#ffe1a3 !important" }} />}
          label={t("officerRoom.tileReconnecting", {
            name: p.name || p.identity,
          })}
          sx={{ bgcolor: "#3b2f0a", color: "#ffe1a3" }}
        />
      ))}
    </Box>
  );
}

function VideoGrid() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gridAutoRows: "1fr",
        gap: 8,
        padding: 8,
        overflowY: "auto",
        position: "relative",
      }}
    >
      <TileReconnecting />
      <LKTrackLoop tracks={tracks}>
        <LKParticipantTile />
      </LKTrackLoop>
    </div>
  );
}

interface CapturedImage {
  id: string;
  url?: string | null;
  caption?: string | null;
}

/** Capture bar + captured-stills strip. Must render inside <LiveKitRoom>. */
const CaptureBar: FC<{
  orgId: string;
  meetingId: string;
  images: CapturedImage[];
  onCaptured: () => void;
}> = ({ orgId, meetingId, images, onCaptured }) => {
  const { t } = useTranslation("connect");
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: true },
  );
  const [busy, setBusy] = useState(false);

  const capture = async () => {
    // Prefer a remote participant's camera (the interviewee).
    const remote = cameraTracks.filter(
      (t) => !t.participant.isLocal && (t.publication as any)?.track,
    );
    const chosen = remote[0] ?? cameraTracks[0];
    const mst = (chosen?.publication as any)?.track?.mediaStreamTrack as
      | MediaStreamTrack
      | undefined;
    if (!mst) {
      toast.info(t("officerRoom.capture.noVideo"));
      return;
    }
    setBusy(true);
    try {
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      // New MediaStream wraps the live track without cloning; never stop it.
      video.srcObject = new MediaStream([mst]);
      await video.play().catch(() => {});
      if (!video.videoWidth) {
        await new Promise<void>((r) => {
          video.onloadedmetadata = () => r();
          setTimeout(r, 1500);
        });
      }
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      canvas
        .getContext("2d")
        ?.drawImage(video, 0, 0, canvas.width, canvas.height);
      video.srcObject = null;
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);

      await postConnectImage({
        path: { org_id: orgId, meeting_id: meetingId },
        body: { image_base64: dataUrl, content_type: "image/jpeg" },
      });
      toast.success(t("officerRoom.capture.success"));
      onCaptured();
    } catch {
      toast.error(t("officerRoom.capture.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 72,
        left: 0,
        right: 0,
        px: 2,
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        pointerEvents: "none",
      }}
    >
      <Button
        variant="contained"
        color="secondary"
        startIcon={<CameraAlt />}
        disabled={busy}
        onClick={capture}
        sx={{ pointerEvents: "auto" }}
      >
        {busy
          ? t("officerRoom.capture.capturing")
          : t("officerRoom.capture.button")}
      </Button>
      {images.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ overflowX: "auto", pointerEvents: "auto", pb: 0.5 }}
        >
          {images.map((img) =>
            img.url ? (
              <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                <Box
                  component="img"
                  src={img.url}
                  alt={img.caption ?? t("officerRoom.capture.imageAlt")}
                  sx={{
                    height: 48,
                    borderRadius: 1,
                    border: "2px solid #fff",
                    display: "block",
                  }}
                />
              </a>
            ) : null,
          )}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Officer monitor panel (Phase H/I/J/K): interruption alerts, per-recipient
 * media controls (mute / disable video / remove), the observer roster (hidden
 * from the subject), and the subject's live location. Polls the connect API;
 * must render inside <LiveKitRoom> for useParticipants().
 */
/** Readiness flag conveyed by colour AND glyph AND an aria-label (never
 *  colour-only), for the waiting-room readiness summary. */
const ReadyFlag: FC<{ label: string; ok: boolean }> = ({ label, ok }) => {
  const { t } = useTranslation("connect");
  return (
    <span
      aria-label={
        ok
          ? t("officerRoom.readiness.ariaYes", { label })
          : t("officerRoom.readiness.ariaNo", { label })
      }
      style={{ color: ok ? "#5fd48a" : "#f0a500" }}
    >
      {label} {ok ? "✓" : "✗"}
    </span>
  );
};

const MonitorPanel: FC<{
  orgId: string;
  meetingId: string;
  invitations: ConnectInvitationModel[];
  isMobile: boolean;
  onClose: () => void;
}> = ({ orgId, meetingId, invitations, isMobile, onClose }) => {
  const { t } = useTranslation("connect");
  const participants = useParticipants();
  const [events, setEvents] = useState<ConnectAuditEventModel[]>([]);
  const [pings, setPings] = useState<ConnectLocationPingModel[]>([]);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);

  // Invitations are polled by the parent (so the waiting-room badge stays live
  // when this panel is closed); here we only poll audit + location.
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const [aud, loc] = await Promise.all([
          getConnectAudit({ path: { org_id: orgId, meeting_id: meetingId } }),
          getConnectLocations({
            path: { org_id: orgId, meeting_id: meetingId },
          }),
        ]);
        if (stop) return;
        setEvents(aud.data?.events ?? []);
        setPings(loc.data?.pings ?? []);
      } catch {
        /* transient — keep prior data */
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [orgId, meetingId]);

  const invById = new Map(invitations.map((i) => [i.id, i]));
  const nameFor = (invitationId?: string | null) =>
    (invitationId && invById.get(invitationId)?.name) ||
    t("officerRoom.monitor.participantFallback");
  const participantTypeLabel = (type: string) =>
    t(`roles.${type}`, { defaultValue: type });
  const statusLabel = (status: string) =>
    STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status;

  const roomInvitees = participants.filter(
    (p) => !p.isLocal && p.identity.startsWith(INVITEE_PREFIX),
  );
  const observers = invitations.filter(
    (i) => i.participant_type === "observer",
  );
  // Only people who actually reached the waiting room (granted camera/mic) are
  // awaiting an admit decision; earlier states are pre-arrival progress.
  // paused_at splits returning participants from first-time arrivals.
  const waitingReturning = invitations.filter(
    (i) => i.status === "waiting_room" && i.paused_at,
  );
  const waitingNew = invitations.filter(
    (i) => i.status === "waiting_room" && !i.paused_at,
  );
  const joining = invitations.filter(
    (i) => JOINING_STATUS_LABEL_KEYS[i.status] !== undefined,
  );

  // Latest location sample per invitation (pings arrive newest-first).
  const latestByInv = new Map<string, ConnectLocationPingModel>();
  for (const p of pings)
    if (!latestByInv.has(p.invitation_id)) latestByInv.set(p.invitation_id, p);
  const latestLocations = [...latestByInv.values()];

  const alerts = events
    .filter((e) => ALERT_LABEL_KEYS[e.event_type])
    .slice(-10)
    .reverse();

  const setMedia = async (
    invitationId: string,
    body: { audio_muted?: boolean; video_muted?: boolean },
  ) => {
    try {
      await postConnectParticipantMedia({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          invitation_id: invitationId,
        },
        body,
      });
    } catch {
      toast.error(t("officerRoom.monitor.controlFailed"));
    }
  };

  // Camera is optional at join, so a dark tile can't be forced on — ask.
  const requestCamera = async (invitationId: string) => {
    try {
      await postConnectParticipantCameraRequest({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          invitation_id: invitationId,
        },
      });
      toast.success(t("officerRoom.monitor.cameraRequestSent"));
    } catch (e: any) {
      toast.error(
        e?.response?.data?.console_msg ??
          t("officerRoom.monitor.cameraRequestError"),
      );
    }
  };

  const removeParticipant = async (invitationId: string) => {
    setRemoveTarget(null);
    try {
      await postConnectInvitationDecision({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          invitation_id: invitationId,
        },
        body: { action: "remove" },
      });
      toast.success(t("officerRoom.monitor.removeSuccess"));
    } catch {
      toast.error(t("officerRoom.monitor.removeError"));
    }
  };

  const decide = async (invitationId: string, action: "admit" | "decline") => {
    try {
      await postConnectInvitationDecision({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          invitation_id: invitationId,
        },
        body: { action },
      });
      toast.success(
        action === "admit"
          ? t("officerRoom.monitor.admitSuccess")
          : t("officerRoom.monitor.declineSuccess"),
      );
    } catch (e: unknown) {
      toast.error(consoleMsgFrom(e) ?? t("officerRoom.monitor.decisionError"));
    }
  };

  const renderWaitingRow = (i: ConnectInvitationModel) => (
    <Box key={i.id} sx={{ border: "1px solid #2a2c40", borderRadius: 1, p: 1 }}>
      <Typography variant="body2" noWrap>
        {i.name} · {participantTypeLabel(i.participant_type)}
      </Typography>
      <Typography variant="caption" sx={{ display: "block" }}>
        <ReadyFlag
          label={t("officerRoom.readiness.notice")}
          ok={!!i.notice_acknowledged_at}
        />{" "}
        ·{" "}
        <ReadyFlag
          label={t("officerRoom.readiness.camera")}
          ok={i.camera_granted}
        />{" "}
        ·{" "}
        <ReadyFlag
          label={t("officerRoom.readiness.microphone")}
          ok={i.microphone_granted}
        />{" "}
        ·{" "}
        <ReadyFlag
          label={t("officerRoom.readiness.location")}
          ok={i.location_granted}
        />
      </Typography>
      <Stack direction="row" spacing={0.5} mt={0.5}>
        <Button
          size="small"
          variant="contained"
          onClick={() => decide(i.id, "admit")}
        >
          {t("officerRoom.monitor.admit")}
        </Button>
        <Button
          size="small"
          color="inherit"
          onClick={() => decide(i.id, "decline")}
          sx={{ color: "#e88" }}
        >
          {t("officerRoom.monitor.decline")}
        </Button>
      </Stack>
    </Box>
  );

  const label = { color: "#9aa" as const };
  const muted = { color: "#a9adbd" as const };

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : 340,
        maxWidth: isMobile ? "100%" : "40vw",
        maxHeight: isMobile ? "45vh" : "none",
        flexShrink: 0,
        bgcolor: "#141522",
        color: "#e8e8ef",
        borderLeft: isMobile ? "none" : "1px solid #2a2c40",
        borderTop: isMobile ? "1px solid #2a2c40" : "none",
        overflowY: "auto",
        p: 1.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
          {t("officerRoom.monitor.title")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ ...muted, width: 40, height: 40, borderRadius: 1 }}
          aria-label={t("officerRoom.monitor.closePanel")}
        >
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Typography variant="caption" sx={label}>
        {t("officerRoom.monitor.returningFromPause")}
      </Typography>
      {waitingReturning.length === 0 ? (
        <Typography variant="body2" sx={{ ...muted, mb: 1 }}>
          {t("officerRoom.monitor.none")}
        </Typography>
      ) : (
        <Stack spacing={1} mb={1}>
          {waitingReturning.map(renderWaitingRow)}
        </Stack>
      )}
      <Divider sx={{ borderColor: "#2a2c40", my: 1 }} />

      <Typography variant="caption" sx={label}>
        {t("officerRoom.monitor.waitingRoom")}
      </Typography>
      {waitingNew.length === 0 ? (
        <Typography variant="body2" sx={{ ...muted, mb: 1 }}>
          {t("officerRoom.monitor.noOneWaiting")}
        </Typography>
      ) : (
        <Stack spacing={1} mb={1}>
          {waitingNew.map(renderWaitingRow)}
        </Stack>
      )}
      <Divider sx={{ borderColor: "#2a2c40", my: 1 }} />

      {joining.length > 0 && (
        <>
          <Typography variant="caption" sx={label}>
            {t("officerRoom.monitor.invited")}
          </Typography>
          <Stack spacing={1} mt={0.5} mb={1}>
            {joining.map((i) => (
              <Box
                key={i.id}
                sx={{ border: "1px dashed #2a2c40", borderRadius: 1, p: 1 }}
              >
                <Typography variant="body2" noWrap>
                  {i.name} · {participantTypeLabel(i.participant_type)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ ...muted, display: "block" }}
                >
                  {t(JOINING_STATUS_LABEL_KEYS[i.status])}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Divider sx={{ borderColor: "#2a2c40", my: 1 }} />
        </>
      )}

      <Typography variant="caption" sx={label}>
        {t("officerRoom.monitor.alerts")}
      </Typography>
      {alerts.length === 0 ? (
        <Typography variant="body2" sx={{ ...muted, mb: 1 }}>
          {t("officerRoom.monitor.noInterruptions")}
        </Typography>
      ) : (
        <Stack spacing={0.5} mb={1}>
          {alerts.map((e) => (
            <Box key={e.id} sx={{ fontSize: 12, display: "flex", gap: 1 }}>
              <Warning
                fontSize="inherit"
                sx={{ color: "#f0a500", mt: "2px" }}
              />
              <span>
                <strong>{t(ALERT_LABEL_KEYS[e.event_type])}</strong> —{" "}
                {nameFor(e.invitation_id)} ·{" "}
                {new Date(e.time_created).toLocaleTimeString()}
              </span>
            </Box>
          ))}
        </Stack>
      )}
      <Divider sx={{ borderColor: "#2a2c40", my: 1 }} />

      <Typography variant="caption" sx={label}>
        {t("officerRoom.monitor.inTheRoom")}
      </Typography>
      {roomInvitees.length === 0 ? (
        <Typography variant="body2" sx={{ ...muted, mb: 1 }}>
          {t("officerRoom.monitor.noRecipients")}
        </Typography>
      ) : (
        <Stack spacing={1} mb={1}>
          {roomInvitees.map((p) => {
            const invId = p.identity.slice(INVITEE_PREFIX.length);
            const inv = invById.get(invId);
            const micOn = p.isMicrophoneEnabled;
            const camOn = p.isCameraEnabled;
            return (
              <Box
                key={p.identity}
                sx={{ border: "1px solid #2a2c40", borderRadius: 1, p: 1 }}
              >
                <Typography variant="body2" noWrap>
                  {inv?.name ?? p.name ?? t("officerRoom.monitor.participant")}
                  {inv
                    ? ` · ${participantTypeLabel(inv.participant_type)}`
                    : ""}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5}>
                  <Tooltip
                    title={
                      micOn
                        ? t("officerRoom.monitor.muteMic")
                        : t("officerRoom.monitor.unmuteMic")
                    }
                  >
                    <IconButton
                      size="small"
                      sx={{ color: "#e8e8ef" }}
                      onClick={() => setMedia(invId, { audio_muted: micOn })}
                    >
                      {micOn ? (
                        <Mic fontSize="small" />
                      ) : (
                        <MicOff fontSize="small" color="warning" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      camOn
                        ? t("officerRoom.monitor.disableCamera")
                        : t("officerRoom.monitor.enableCamera")
                    }
                  >
                    <IconButton
                      size="small"
                      sx={{ color: "#e8e8ef" }}
                      onClick={() => setMedia(invId, { video_muted: camOn })}
                    >
                      {camOn ? (
                        <Videocam fontSize="small" />
                      ) : (
                        <VideocamOff fontSize="small" color="warning" />
                      )}
                    </IconButton>
                  </Tooltip>
                  {!camOn && inv?.participant_type !== "observer" && (
                    <Tooltip
                      title={t("officerRoom.monitor.requestCameraTooltip")}
                    >
                      <IconButton
                        size="small"
                        sx={{ color: "#e8e8ef" }}
                        aria-label={t("officerRoom.monitor.requestCamera")}
                        onClick={() => requestCamera(invId)}
                      >
                        <VideoCall fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("officerRoom.monitor.removeTooltip")}>
                    <IconButton
                      size="small"
                      sx={{ color: "#e88" }}
                      aria-label={t("officerRoom.monitor.removeAriaLabel")}
                      onClick={() => setRemoveTarget(invId)}
                    >
                      <PersonRemove fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
      <Divider sx={{ borderColor: "#2a2c40", my: 1 }} />

      <Typography variant="caption" sx={label}>
        {t("officerRoom.monitor.observers")}
      </Typography>
      {observers.length === 0 ? (
        <Typography variant="body2" sx={{ ...muted, mb: 1 }}>
          {t("officerRoom.monitor.none")}
        </Typography>
      ) : (
        <Stack spacing={0.5} mb={1}>
          {observers.map((o) => (
            <Box
              key={o.id}
              sx={{
                fontSize: 13,
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <span>{o.name}</span>
              <Chip
                size="small"
                label={statusLabel(o.status)}
                sx={{ height: 18, fontSize: 10 }}
              />
            </Box>
          ))}
        </Stack>
      )}
      <Divider sx={{ borderColor: "#2a2c40", my: 1 }} />

      <Typography variant="caption" sx={label}>
        {t("officerRoom.monitor.subjectLocation")}
      </Typography>
      {latestLocations.length === 0 ? (
        <Typography variant="body2" sx={muted}>
          {t("officerRoom.monitor.noLocation")}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {latestLocations.map((l) => (
            <Box key={l.id} sx={{ fontSize: 12 }}>
              <strong>{nameFor(l.invitation_id)}</strong> ·{" "}
              {new Date(l.captured_at).toLocaleTimeString()}
              <br />
              {l.available && l.latitude != null && l.longitude != null ? (
                <a
                  href={`https://maps.google.com/?q=${l.latitude},${l.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#7aa7ff" }}
                >
                  {t("officerRoom.monitor.coordinates", {
                    latitude: l.latitude.toFixed(5),
                    longitude: l.longitude.toFixed(5),
                  })}
                </a>
              ) : (
                <span style={{ color: "#f0a500" }}>
                  {t("officerRoom.monitor.locationUnavailable")}
                </span>
              )}
            </Box>
          ))}
        </Stack>
      )}
      <ConfirmDialog
        open={removeTarget !== null}
        title={t("officerRoom.removeConfirm.title")}
        message={t("officerRoom.removeConfirm.message")}
        confirmLabel={t("officerRoom.removeConfirm.confirm")}
        destructive
        onConfirm={() => removeTarget && removeParticipant(removeTarget)}
        onCancel={() => setRemoveTarget(null)}
      />
    </Box>
  );
};

const OfficerRoomDialog: FC<Props> = ({ open, onClose, orgId, meeting }) => {
  const { t } = useTranslation("connect");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [token, setToken] = useState<string | null>(null);
  const [wsUrl, setWsUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<CapturedImage[]>([]);
  const [paused, setPaused] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [pausePickerOpen, setPausePickerOpen] = useState(false);
  // Shows the break room instead of the live call until resumed.
  const [selfPaused, setSelfPaused] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [showMonitor, setShowMonitor] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitations, setInvitations] = useState<ConnectInvitationModel[]>([]);
  const meetingId = meeting?.id ?? null;
  const { elapsed } = useSessionClock(open && !!token);
  const { isDevEnv } = useDevEnvironment();
  const orgConfig = getServiceConfig(orgId, isDevEnv);
  const [crestFailed, setCrestFailed] = useState(false);
  const connectedOnce = useRef(false);
  const onHoldCount = invitations.filter((i) => i.status === "paused").length;
  const waitingCount = invitations.filter(
    (i) => i.status === "waiting_room",
  ).length;

  // Poll the invitation list at the dialog level (not just in the Participants
  // panel) so the waiting-room count badge stays live even when the panel is
  // collapsed.
  useEffect(() => {
    if (!open || !meetingId || !token) return;
    let stop = false;
    const load = async () => {
      try {
        const res = await getConnectInvitations({
          path: { org_id: orgId, meeting_id: meetingId },
        });
        if (!stop) setInvitations(res.data?.invitations ?? []);
      } catch {
        /* transient — keep prior data */
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [open, meetingId, orgId, token]);

  const loadImages = useCallback(async () => {
    if (!meetingId) return;
    try {
      const res = await getConnectImages({
        path: { org_id: orgId, meeting_id: meetingId },
      });
      setImages(res.data?.images ?? []);
    } catch {
      /* non-fatal */
    }
  }, [orgId, meetingId]);

  const [elsewhereOpen, setElsewhereOpen] = useState(false);
  const fetchToken = useCallback(
    async (takeOver = false): Promise<boolean> => {
      if (!meetingId) return false;
      try {
        const res = await postConnectMeetingToken({
          path: { org_id: orgId, meeting_id: meetingId },
          query: { take_over: takeOver },
        });
        setToken(res.data!.token);
        setWsUrl(res.data!.livekit_ws_url);
        return true;
      } catch (e: any) {
        if (e?.response?.status !== 409) return false;
        setElsewhereOpen(true);
        return true;
      }
    },
    [orgId, meetingId],
  );

  useEffect(() => {
    if (!open || !meetingId) return;
    let cancelled = false;
    connectedOnce.current = false;
    setToken(null);
    setError(null);
    setSelfPaused(false);
    setPaused(meeting?.paused ?? false);
    fetchToken().then((ok) => {
      if (!cancelled && !ok) setError(t("officerRoom.errors.startFailed"));
    });
    loadImages();
    return () => {
      cancelled = true;
    };
  }, [open, meetingId, fetchToken, loadImages, meeting?.paused, t]);

  const [ending, setEnding] = useState(false);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [endMenuAnchor, setEndMenuAnchor] = useState<HTMLElement | null>(null);
  const strongBorder = alpha(theme.palette.common.white, 0.23);
  const navTextSx = {
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    "& .MuiButton-startIcon > svg": { fontSize: 18 },
  } as const;
  const navButtonSx = {
    ...navTextSx,
    height: 40,
    px: 2,
    color: "common.white",
    borderColor: strongBorder,
  } as const;
  const handleClose = () => {
    setLeaveConfirmOpen(false);
    setElsewhereOpen(false);
    setToken(null);
    setWsUrl(null);
    setImages([]);
    onClose();
  };
  // Every way out of the call confirms first: the X, Escape, the backdrop.
  const requestLeave = () => {
    if (!token) {
      handleClose();
      return;
    }
    setLeaveConfirmOpen(true);
  };

  // Closing the tab while connected also leaves — let the browser ask.
  useEffect(() => {
    if (!open || !token) return;
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [open, token]);

  // Also releases anyone held by a selective pause, which never sets
  // meeting.paused.
  const resume = async () => {
    if (!meetingId) return;
    setPausing(true);
    try {
      await postConnectMeetingPause({
        path: { org_id: orgId, meeting_id: meetingId },
        body: { paused: false },
      });
      setPaused(false);
      setInvitations((invs) =>
        invs.map((i) =>
          i.status === "paused" ? { ...i, status: "waiting_room" } : i,
        ),
      );
    } catch {
      toast.error(t("officerRoom.errors.resumeFailed"));
    } finally {
      setPausing(false);
    }
  };

  const handleSelfPaused = () => {
    setToken(null);
    setWsUrl(null);
    setSelfPaused(true);
  };

  const handleResumeFromBreakRoom = async () => {
    if (!meetingId) return;
    setResuming(true);
    try {
      const res = await postConnectMeetingPause({
        path: { org_id: orgId, meeting_id: meetingId },
        body: { paused: false },
      });
      setPaused(res.data?.paused ?? false);
      setSelfPaused(false);
      const ok = await fetchToken();
      if (!ok) {
        setError(t("officerRoom.errors.reconnectFailed"));
      }
    } catch {
      toast.error(t("officerRoom.errors.resumeFailed"));
    } finally {
      setResuming(false);
    }
  };

  const handleEnd = async () => {
    if (!meetingId) return;
    setEndConfirmOpen(false);
    setEnding(true);
    try {
      await postConnectMeetingEnd({
        path: { org_id: orgId, meeting_id: meetingId },
      });
      toast.success(t("officerRoom.end.success"));
      handleClose();
    } catch {
      toast.error(t("officerRoom.end.error"));
    } finally {
      setEnding(false);
    }
  };

  return (
    <Dialog open={open} onClose={requestLeave} fullScreen>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <Box
          px={2}
          py={1}
          sx={{
            backgroundColor: "#1A1B2B",
            color: "#fff",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            rowGap: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={{ xs: 1.25, sm: 1.75 }}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: { xs: 28, sm: 40 },
                height: { xs: 32, sm: 46 },
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              {orgConfig.logoURL && !crestFailed ? (
                <Box
                  component="img"
                  src={orgConfig.logoURL}
                  alt=""
                  onError={() => setCrestFailed(true)}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <ShieldOutlined />
              )}
            </Box>
            <Stack spacing={{ xs: 0.25, sm: 0.5 }} sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: { xs: 16, sm: 20 },
                  fontWeight: { xs: 500, sm: 400 },
                  lineHeight: 1.25,
                  letterSpacing: "0.005em",
                }}
              >
                {orgConfig.fullName ?? "RubiCONNECT"}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                divider={
                  <Divider
                    orientation="vertical"
                    sx={{
                      height: 12,
                      alignSelf: "center",
                      borderColor: "currentColor",
                      opacity: 0.5,
                    }}
                  />
                }
                sx={{
                  minWidth: 0,
                  color: "#9aa",
                  fontSize: { xs: 12, sm: 13 },
                }}
              >
                <Typography
                  fontSize="inherit"
                  fontWeight={500}
                  color="common.white"
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {meeting?.title ?? t("officerRoom.header.sessionFallback")}
                </Typography>
                {meeting?.occurrence_number && (
                  <Typography fontSize="inherit" noWrap sx={{ flexShrink: 0 }}>
                    {t("officerRoom.header.reference", {
                      reference: meeting.occurrence_number,
                    })}
                  </Typography>
                )}
                {token && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ flexShrink: 0 }}
                  >
                    <AccessTime sx={{ fontSize: 14 }} />
                    <Typography
                      fontSize="inherit"
                      noWrap
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {t("officerRoom.header.elapsed", { elapsed })}
                    </Typography>
                  </Stack>
                )}
                {paused && (
                  <Typography
                    fontSize="inherit"
                    fontWeight={600}
                    color="warning.main"
                    sx={{ flexShrink: 0 }}
                  >
                    {t("officerRoom.header.paused")}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              startIcon={<PersonAddAlt1Outlined />}
              disabled={!token}
              onClick={() => setInviteOpen(true)}
              sx={navButtonSx}
            >
              {t("officerRoom.header.invite")}
            </Button>
            <Button
              variant="outlined"
              startIcon={paused ? <PlayArrow /> : <Pause />}
              disabled={pausing || !token}
              onClick={() =>
                paused ? void resume() : setPausePickerOpen(true)
              }
              sx={navButtonSx}
            >
              {paused
                ? t("officerRoom.header.resume")
                : t("officerRoom.header.pause")}
            </Button>
            {!paused && onHoldCount > 0 && (
              <Button
                variant="outlined"
                startIcon={<PlayArrow />}
                disabled={pausing || !token}
                onClick={() => void resume()}
                sx={navButtonSx}
              >
                {t("officerRoom.header.resumePaused", { count: onHoldCount })}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PeopleOutline />}
              aria-pressed={showMonitor}
              onClick={() => setShowMonitor((s) => !s)}
              sx={{
                ...navButtonSx,
                gap: 1,
                backgroundColor: showMonitor
                  ? alpha(theme.palette.primary.main, 0.16)
                  : "transparent",
              }}
            >
              {t("officerRoom.header.participants")}
              {waitingCount > 0 && (
                <Box
                  component="span"
                  aria-label={t("officerRoom.header.waitingCount", {
                    count: waitingCount,
                  })}
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    boxSizing: "border-box",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: 700,
                    backgroundColor: "warning.main",
                    color: "warning.contrastText",
                  }}
                >
                  {waitingCount}
                </Box>
              )}
            </Button>
            <Divider
              orientation="vertical"
              sx={{ height: 28, mx: 1, borderColor: strongBorder }}
            />
            <ButtonGroup
              variant="contained"
              color="error"
              disableElevation
              disabled={ending}
              sx={{ height: 40 }}
            >
              <Button
                startIcon={<Logout />}
                onClick={() => setEndConfirmOpen(true)}
                sx={{ ...navTextSx, px: 2 }}
              >
                {ending
                  ? t("officerRoom.header.ending")
                  : t("officerRoom.header.endSession")}
              </Button>
              <Button
                aria-label={t("officerRoom.header.moreLeaveOptions")}
                aria-haspopup="menu"
                aria-expanded={Boolean(endMenuAnchor)}
                aria-controls={endMenuAnchor ? "end-session-menu" : undefined}
                onClick={(e) => setEndMenuAnchor(e.currentTarget)}
                sx={{
                  width: 36,
                  minWidth: 36,
                  p: 0,
                  "&&": {
                    borderLeft: `1px solid ${darken(theme.palette.error.main, 0.3)}`,
                  },
                }}
              >
                <KeyboardArrowDown sx={{ fontSize: 16 }} />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={endMenuAnchor}
              slotProps={{ list: { id: "end-session-menu" } }}
              open={!!endMenuAnchor}
              onClose={() => setEndMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setEndMenuAnchor(null);
                  requestLeave();
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("officerRoom.header.leaveRoom")}
                  secondary={t("officerRoom.header.leaveRoomHint")}
                />
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        <Box
          sx={{ flex: 1, position: "relative", display: "flex", minHeight: 0 }}
        >
          {selfPaused ? (
            <OfficerBreakRoom
              busy={resuming}
              onResume={() => void handleResumeFromBreakRoom()}
            />
          ) : error ? (
            <Box sx={{ m: "auto", textAlign: "center" }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : !token || !wsUrl ? (
            <Box sx={{ m: "auto", textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <LKRoom
              token={token}
              serverUrl={wsUrl}
              options={LK_ROOM_OPTIONS}
              connectOptions={LK_CONNECT_OPTIONS}
              data-lk-theme="default"
              connect
              video
              audio
              style={{
                flex: 1,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                minHeight: 0,
              }}
              onError={(e: Error) => {
                console.error("LiveKit error:", e);
                toast.error(
                  t("officerRoom.errors.callError", { message: e.message }),
                );
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  minHeight: 0,
                }}
              >
                <DisconnectOnPageHide />
                <DisconnectRouter
                  onPaused={handleSelfPaused}
                  onDisconnected={handleClose}
                />
                <ReconnectingBanner />
                <VideoGrid />
                <RoomAudioRenderer />
                <CaptureBar
                  orgId={orgId}
                  meetingId={meetingId!}
                  images={images}
                  onCaptured={loadImages}
                />
                <ControlBar
                  controls={{
                    camera: true,
                    microphone: true,
                    screenShare: true,
                    leave: false,
                  }}
                />
              </Box>
              {showMonitor && (
                <MonitorPanel
                  orgId={orgId}
                  meetingId={meetingId!}
                  invitations={invitations}
                  isMobile={isMobile}
                  onClose={() => setShowMonitor(false)}
                />
              )}
            </LKRoom>
          )}
        </Box>
      </Box>
      {/* Invite more participants without leaving the room (requirements 2.4). */}
      <InvitationsDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        orgId={orgId}
        meetingId={meetingId}
        meetingTitle={meeting?.title}
      />
      <PausePickerDialog
        open={pausePickerOpen}
        onClose={() => setPausePickerOpen(false)}
        orgId={orgId}
        meetingId={meetingId ?? ""}
        admitted={invitations.filter((i) => i.status === "admitted")}
        onPaused={(target) => {
          if (target.kind === "all") setPaused(true);
        }}
      />
      <ConfirmDialog
        open={leaveConfirmOpen}
        title={t("officerRoom.leaveConfirm.title")}
        message={t("officerRoom.leaveConfirm.message")}
        confirmLabel={t("officerRoom.leaveConfirm.confirm")}
        onConfirm={handleClose}
        onCancel={() => setLeaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={elsewhereOpen}
        title="Already in this call"
        message="You're connected to this interview on another device. Move the call here? Your other device will be disconnected."
        confirmLabel="Move call here"
        onConfirm={() => {
          setElsewhereOpen(false);
          void fetchToken(true).then((ok) => {
            if (!ok) setError("Could not start the session.");
          });
        }}
        onCancel={handleClose}
      />
      <ConfirmDialog
        open={endConfirmOpen}
        title={t("officerRoom.endConfirm.title")}
        message={t("officerRoom.endConfirm.message")}
        confirmLabel={t("officerRoom.endConfirm.confirm")}
        destructive
        busy={ending}
        onConfirm={handleEnd}
        onCancel={() => setEndConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default OfficerRoomDialog;
